import { createContext, forwardRef, useContext, useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { cn } from "../../lib/cn";

type FieldCtx = { id: string; describedBy?: string; invalid: boolean };
const Ctx = createContext<FieldCtx | null>(null);

/** Label above (12 px bold), optional hint, 6 px gap (§3.3). Inputs inside pick up the id,
 * aria-describedby and aria-invalid from here, so labels are always wired. */
export function Field({ label, hint, error, children, className, id: idProp, optional }: { label: ReactNode; hint?: ReactNode; error?: ReactNode; children: ReactNode; className?: string; id?: string; optional?: boolean }) {
  const auto = useId();
  const id = idProp ?? auto;
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const describedBy = [hintId, errId].filter(Boolean).join(" ") || undefined;
  return (
    <Ctx.Provider value={{ id, describedBy, invalid: !!error }}>
      <div className={cn("flex flex-col gap-1.5 min-w-0", className)}>
        <label htmlFor={id} className="field-label">
          {label}
          {optional && <span className="font-medium text-neutral-500"> (optional)</span>}
          {hint && (
            <span id={hintId} className="font-medium text-neutral-500">
              {" "}
              {hint}
            </span>
          )}
        </label>
        {children}
        {error && (
          <p id={errId} className="text-sm font-medium text-danger-ink">
            {error}
          </p>
        )}
      </div>
    </Ctx.Provider>
  );
}

function useFieldProps<T extends { id?: string; "aria-describedby"?: string; "aria-invalid"?: InputHTMLAttributes<HTMLInputElement>["aria-invalid"] }>(props: T): T {
  const ctx = useContext(Ctx);
  if (!ctx) return props;
  return { ...props, id: props.id ?? ctx.id, "aria-describedby": props["aria-describedby"] ?? ctx.describedBy, "aria-invalid": props["aria-invalid"] ?? (ctx.invalid || undefined) };
}

type InputProps = InputHTMLAttributes<HTMLInputElement> & { size?: never; inputSize?: "md" | "lg" };

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input({ className, inputSize = "md", ...rest }, ref) {
  const p = useFieldProps(rest);
  return <input ref={ref} className={cn("input", inputSize === "lg" && "input-lg", className)} {...p} />;
});

/** Money/quantity entry: numeric keypad on touch (§16.4), tabular digits, no spinner arrows. */
export const NumberInput = forwardRef<HTMLInputElement, InputProps & { decimals?: boolean }>(function NumberInput({ className, decimals = true, inputSize = "md", ...rest }, ref) {
  const p = useFieldProps(rest);
  return <input ref={ref} type="number" inputMode={decimals ? "decimal" : "numeric"} className={cn("input tnum", inputSize === "lg" && "input-lg", className)} {...p} />;
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement> & { inputSize?: "md" | "lg" }>(function Select({ className, inputSize = "md", children, ...rest }, ref) {
  const p = useFieldProps(rest);
  return (
    <select ref={ref} className={cn("input", inputSize === "lg" && "input-lg", className)} {...p}>
      {children}
    </select>
  );
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, rows = 2, ...rest }, ref) {
  const p = useFieldProps(rest);
  return <textarea ref={ref} rows={rows} className={cn("input", className)} {...p} />;
});
